import { FC, useState } from 'react';
import { Button } from '@/components/ui/button';
import {
	Dialog,
	DialogContent,
	DialogTitle,
	DialogDescription,
	DialogHeader,
	DialogFooter,
	DialogClose,
} from '@/components/ui/dialog';
import Cancel_01_Icon from '@/components/icons/Cancel_01_Icon';
import { t } from 'i18next';
import { cn } from '@/lib/utils';
import ArrowLeft_01_SharpIcon from '@/components/icons/ArrowLeft_01_SharpIcon';
import ArrowRight_01_SharpIcon from '@/components/icons/ArrowRight_01_SharpIcon';
import SendIcon from '@/components/icons/SendIcon';
import EInfoModal from '@/components/everridge/EInfoModal';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {
	NotificationCreateFormData,
	NotificationCreateFormSchema,
} from '@/features/notifications/validators/NotificationCreateValidator';
import { GetHotelListItemDto } from '@/features/hotels/types/hotels-dto';
import NotificationCreateStep1 from '@/features/notifications/componentns/NotificationCreate/steps/NotificationCreateStep1';
import NotificationCreateChart from '@/features/notifications/componentns/NotificationCreate/NotificationCreateChart';
import NotificationCreateStep2 from '@/features/notifications/componentns/NotificationCreate/steps/NotificationCreateStep2';
import NotificationCreateStep3 from '@/features/notifications/componentns/NotificationCreate/steps/NotificationCreateStep3';
import { CreateNotificationRequestDto } from '@/features/notifications/types/notifications-dto';
import { useNotificationDetailsApi } from '@/features/notifications/hooks/useNotificationDetailsApi';
import useMediaQuery from '@/hooks/useMediaQuery';
import { AppConfig } from '@/config/app.config';

type Props = {
	onClose: () => void;
};

const NotificationCreate: FC<Props> = ({ onClose }) => {
	const [isOpen, setIsOpen] = useState(true);
	const [formStep, setFormStep] = useState(0);
	const [showDiscardMessage, setShowDiscardMessage] = useState(false);
	const [hotelIds, setHotels] = useState<GetHotelListItemDto[]>([]);

	const isMobile = useMediaQuery(AppConfig.mediaQueries.mobile);
	const { createNotification } = useNotificationDetailsApi();

	const { control, handleSubmit, setValue } =
		useForm<NotificationCreateFormData>({
			resolver: zodResolver(NotificationCreateFormSchema),
			defaultValues: {
				isRequired: false,
			},
		});

	const handleClose = () => {
		setIsOpen(false);
		setShowDiscardMessage(false);
		setTimeout(onClose, 300);
	};

	const handleFormSubmit = async (form: NotificationCreateFormData) => {
		const transformedData = {
			...form,
			hotels: form.hotels.map((hotel) => ({
				...hotel,
				apartmentIds: hotel.apartmentIds?.map((apt) => apt.id),
			})),
		};

		const dto: CreateNotificationRequestDto = transformedData;

		await createNotification(dto);
		handleClose();
	};

	const headerContent = [
		t('main.notifications.components.create.steps.hotels'),
		t('main.notifications.components.create.steps.apartments'),
		t('main.notifications.components.create.steps.message'),
	];

	const footerButton = [
		<>
			<Button
				variant='outline'
				onClick={handleClose}>
				{t('elements.buttons.cancel')}
			</Button>
			<Button
				onClick={() => setFormStep(1)}
				disabled={!hotelIds.length}>
				{t('elements.buttons.next')}
				<ArrowRight_01_SharpIcon
					className={cn(
						'stroke-popover-10!',
						!hotelIds.length && 'stroke-popover-400!',
					)}
				/>
			</Button>
		</>,
		<>
			<Button
				variant='outline'
				onClick={() => setFormStep(0)}>
				<ArrowLeft_01_SharpIcon />
				{t('elements.buttons.back')}
			</Button>
			<Button onClick={() => setFormStep(2)}>
				{t('elements.buttons.next')}
				<ArrowRight_01_SharpIcon className='stroke-popover-10' />
			</Button>
		</>,
		<>
			<Button
				variant='outline'
				onClick={() => setFormStep(1)}>
				<ArrowLeft_01_SharpIcon />
				{t('elements.buttons.back')}
			</Button>
			<Button onClick={handleSubmit(handleFormSubmit)}>
				{t('elements.buttons.sendNotification')}
				<SendIcon className='stroke-popover-10' />
			</Button>
		</>,
	];

	return (
		<>
			<Dialog
				open={isOpen}
				onOpenChange={() => setShowDiscardMessage(true)}>
				<DialogContent>
					<DialogHeader>
						<DialogTitle>
							{t('main.notifications.components.create.title')}
							<DialogClose
								className={cn(
									'ml-auto flex items-center justify-center cursor-pointer',
									isMobile &&
										'size-11 border border-popover-100 rounded-full',
								)}>
								<Cancel_01_Icon />
							</DialogClose>
						</DialogTitle>
						<DialogDescription />
					</DialogHeader>
					<div className='min-h-80 flex flex-col gap-6'>
						<div className='flex items-center gap-3'>
							<NotificationCreateChart
								step={formStep + 1}
								maxSteps={headerContent.length}
							/>
							<div className='flex flex-col'>
								<span className='text-xs font-medium text-popover-600'>
									{`${t(
										'main.notifications.components.create.steps.step',
									)} ${formStep + 1} of ${
										headerContent.length
									}`}
								</span>
								<span className='text-sm font-medium text-popover-1000'>
									{headerContent[formStep]}
								</span>
							</div>
						</div>
						{formStep === 0 && (
							<NotificationCreateStep1
								hotels={hotelIds}
								setHotels={setHotels}
								setValue={setValue}
							/>
						)}
						{formStep === 1 && (
							<NotificationCreateStep2
								control={control}
								hotels={hotelIds}
							/>
						)}
						{formStep === 2 && (
							<NotificationCreateStep3 control={control} />
						)}
					</div>
					<DialogFooter
						className={cn(
							'justify-between',
							formStep === 0 && 'justify-end',
							isMobile && 'justify-end mt-auto',
						)}>
						{footerButton[formStep]}
					</DialogFooter>
				</DialogContent>
			</Dialog>
			{showDiscardMessage && (
				<EInfoModal
					label={t(
						'main.notifications.components.create.discard.title',
					)}
					description={t(
						'main.notifications.components.create.discard.description',
					)}
					submitName={t('elements.buttons.discard')}
					onSubmit={handleClose}
					closeName={t('elements.buttons.cancel')}
					onClose={() => setShowDiscardMessage(false)}
				/>
			)}
		</>
	);
};

export default NotificationCreate;
